import { request } from "./apiClient";
import type {
  RecurringExpenseItem,
  RecurringExpenseListResponse,
  RecurringExpenseSummary,
  RecurringExpenseSuggestionItem,
} from "@boo/shared";

export type RecurringExpenseSort = "amount" | "dueDate";

export interface CreateRecurringExpensePayload {
  title: string;
  amount: number;
  category: string;
  frequency: string;
  dayOfMonth?: number;
  month?: number;
}

export async function fetchRecurringExpenses(opts?: {
  sort?: RecurringExpenseSort;
  includeInactive?: boolean;
}): Promise<RecurringExpenseListResponse> {
  const params = new URLSearchParams();
  if (opts?.sort) params.set("sort", opts.sort);
  if (opts?.includeInactive) params.set("includeInactive", "true");
  const qs = params.toString();
  return request<RecurringExpenseListResponse>(
    `/recurring-expenses${qs ? `?${qs}` : ""}`,
  );
}

export async function fetchRecurringExpenseSummary(): Promise<RecurringExpenseSummary> {
  return request<RecurringExpenseSummary>("/recurring-expenses/summary");
}

export async function createRecurringExpense(
  payload: CreateRecurringExpensePayload,
): Promise<RecurringExpenseItem> {
  return request<RecurringExpenseItem>("/recurring-expenses", {
    method: "POST",
    data: payload,
  });
}

export type UpdateRecurringExpensePayload =
  Partial<CreateRecurringExpensePayload>;

export async function deactivateRecurringExpense(
  id: string,
): Promise<RecurringExpenseItem> {
  return request<RecurringExpenseItem>(`/recurring-expenses/${id}/deactivate`, {
    method: "PATCH",
  });
}

export async function updateRecurringExpense(
  id: string,
  payload: UpdateRecurringExpensePayload,
): Promise<RecurringExpenseItem> {
  return request<RecurringExpenseItem>(`/recurring-expenses/${id}`, {
    method: "PATCH",
    data: payload,
  });
}

export async function fetchRecurringExpenseSuggestions(): Promise<
  RecurringExpenseSuggestionItem[]
> {
  return request<RecurringExpenseSuggestionItem[]>(
    "/recurring-expense-suggestions",
  );
}

export async function triggerSuggestionDetection(): Promise<
  RecurringExpenseSuggestionItem[]
> {
  return request<RecurringExpenseSuggestionItem[]>(
    "/recurring-expense-suggestions/detect",
    {
      method: "POST",
    },
  );
}

export async function confirmSuggestion(
  id: string,
): Promise<RecurringExpenseItem> {
  return request<RecurringExpenseItem>(
    `/recurring-expense-suggestions/${id}/confirm`,
    {
      method: "POST",
    },
  );
}

export async function dismissSuggestion(id: string): Promise<void> {
  return request<void>(`/recurring-expense-suggestions/${id}/dismiss`, {
    method: "POST",
  });
}

export interface EditConfirmSuggestionPayload {
  title?: string;
  amount?: number;
  category?: string;
  frequency?: string;
  dayOfMonth?: number;
  month?: number;
}

export async function editAndConfirmSuggestion(
  id: string,
  overrides: EditConfirmSuggestionPayload,
): Promise<RecurringExpenseItem> {
  return request<RecurringExpenseItem>(
    `/recurring-expense-suggestions/${id}/edit-confirm`,
    {
      method: "POST",
      data: overrides,
    },
  );
}
